import React, { useCallback, useEffect, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';

import { notification, Space, Tag, Typography } from 'antd';

import { getPrometheusConfigError, isPrometheusConfigured } from '@/shared/config/prometheus-config';
import { ROUTES } from '@/shared/lib/constants/routes';

import { PROMETHEUS_NAMESPACE } from '@/features/monitoring';

import {
  CpuMetric,
  GaugeMetrics,
  GpuMetrics,
  IOMetrics,
  MemoryMetric,
  PodOverviewMetric,
  WorkerSelector,
} from '@/widgets/monitoring';

import styles from './MonitoringOverviewPage.module.css';

const { Title, Text } = Typography;

export const MonitoringOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedWorker, setSelectedWorker] = useState<string>('');

  const handleWorkerChange = useCallback((worker: string) => {
    setSelectedWorker(worker);
  }, []);

  useEffect(() => {
    if (!isPrometheusConfigured()) {
      notification.error({
        message: getPrometheusConfigError(),
        duration: 4,
      });
      navigate(ROUTES.HOME);
    }
  }, [navigate]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Space direction="vertical" size={4}>
          <Space align="center" size={12}>
            <Title level={2} className={styles.title}>
              시스템 모니터링
            </Title>
            <Tag color="blue">{PROMETHEUS_NAMESPACE}</Tag>
          </Space>
          <Text className={styles.description}>Prometheus 메트릭 기반 실시간 리소스 모니터링 (30초 자동 갱신)</Text>
        </Space>
      </div>

      <ErrorBoundary
        FallbackComponent={(props) => (
          <GaugeMetrics.Error
            {...props}
            title="게이지 메트릭 오류"
            description="게이지 메트릭 데이터를 불러오는 중 오류가 발생했습니다."
          />
        )}>
        <GaugeMetrics.Content />
      </ErrorBoundary>

      <div className={styles.row}>
        <ErrorBoundary
          FallbackComponent={(props) => (
            <CpuMetric.Error
              {...props}
              title="CPU 메트릭 오류"
              description="CPU 메트릭 데이터를 불러오는 중 오류가 발생했습니다."
            />
          )}>
          <CpuMetric.Content />
        </ErrorBoundary>

        <ErrorBoundary
          FallbackComponent={(props) => (
            <MemoryMetric.Error
              {...props}
              title="메모리 메트릭 오류"
              description="메모리 메트릭 데이터를 불러오는 중 오류가 발생했습니다."
            />
          )}>
          <MemoryMetric.Content />
        </ErrorBoundary>
      </div>

      <ErrorBoundary
        FallbackComponent={(props) => (
          <IOMetrics.Error
            {...props}
            title="I/O 메트릭 오류"
            description="I/O 메트릭 데이터를 불러오는 중 오류가 발생했습니다."
          />
        )}>
        <IOMetrics.Content />
      </ErrorBoundary>

      <ErrorBoundary
        FallbackComponent={(props) => (
          <PodOverviewMetric.Error
            {...props}
            title="Pod 개요 테이블 오류"
            description="Pod 개요 테이블 데이터를 불러오는 중 오류가 발생했습니다."
          />
        )}>
        <PodOverviewMetric.Content />
      </ErrorBoundary>

      <ErrorBoundary
        FallbackComponent={(props) => (
          <WorkerSelector.Error
            {...props}
            title="Worker 선택 오류"
            description="사용 가능한 Worker 목록을 불러오는 중 오류가 발생했습니다."
          />
        )}>
        <WorkerSelector.Content title="Worker 선택" onWorkerChange={handleWorkerChange} />
      </ErrorBoundary>

      <ErrorBoundary
        FallbackComponent={(props) => (
          <GpuMetrics.Error
            {...props}
            title="GPU 모니터링 오류"
            description="GPU 모니터링 데이터를 불러오는 중 오류가 발생했습니다."
          />
        )}>
        <GpuMetrics.Content selectedWorker={selectedWorker} />
      </ErrorBoundary>
    </div>
  );
};
